import {
  Archive,
  Eye,
  EyeClosed,
  Pencil,
  Plus,
} from 'lucide-react'

export interface AdminStationToolbarProps {
  /** The station id passed back to every action callback. */
  stationId: string
  /** Open the station in the CMS editor. */
  onEdit?: (stationId: string) => void | Promise<void>
  /** Toggle a learner-shaped preview for the station. */
  onPreview?: (stationId: string) => void | Promise<void>
  /** Add a new subquestion to the station. */
  onAddSubquestion?: (stationId: string) => void | Promise<void>
  /** Soft-delete/archive the station. The server remains the source of truth. */
  onArchive?: (stationId: string) => void | Promise<void>
  /** Changes the preview action label and icon when preview is already open. */
  previewing?: boolean
  disabled?: boolean
  className?: string
}

function invoke(action: ((stationId: string) => void | Promise<void>) | undefined, stationId: string) {
  if (action) void action(stationId)
}

/**
 * Small reusable toolbar for displaying admin actions beside a learner-facing
 * station. Visibility is intentionally controlled by the parent (usually by
 * `useAuthSession().isAdmin`); all writes are still authorized by the API.
 */
export function AdminStationToolbar({
  stationId,
  onEdit,
  onPreview,
  onAddSubquestion,
  onArchive,
  previewing = false,
  disabled = false,
  className = '',
}: AdminStationToolbarProps) {
  const classes = ['admin-inline-toolbar', className].filter(Boolean).join(' ')

  return (
    <div className={classes} role="toolbar" aria-label="أدوات إدارة المحطة">
      {onEdit && (
        <button
          type="button"
          className="admin-inline-toolbar__button"
          disabled={disabled}
          onClick={() => invoke(onEdit, stationId)}
          title="تعديل المحطة"
        >
          <Pencil size={15} aria-hidden="true" />
          <span>تعديل</span>
        </button>
      )}
      {onPreview && (
        <button
          type="button"
          className="admin-inline-toolbar__button"
          disabled={disabled}
          onClick={() => invoke(onPreview, stationId)}
          title={previewing ? 'إغلاق معاينة الطالب' : 'معاينة كطالب'}
          aria-pressed={previewing}
        >
          {previewing ? <EyeClosed size={15} aria-hidden="true" /> : <Eye size={15} aria-hidden="true" />}
          <span>{previewing ? 'إغلاق المعاينة' : 'معاينة'}</span>
        </button>
      )}
      {onAddSubquestion && (
        <button
          type="button"
          className="admin-inline-toolbar__button"
          disabled={disabled}
          onClick={() => invoke(onAddSubquestion, stationId)}
          title="إضافة سؤال فرعي"
        >
          <Plus size={15} aria-hidden="true" />
          <span>إضافة سؤال</span>
        </button>
      )}
      {onArchive && (
        <button
          type="button"
          className="admin-inline-toolbar__button admin-inline-toolbar__button--danger"
          disabled={disabled}
          onClick={() => invoke(onArchive, stationId)}
          title="أرشفة المحطة"
        >
          <Archive size={15} aria-hidden="true" />
          <span>أرشفة</span>
        </button>
      )}
    </div>
  )
}

/** Alias used by learner-facing station views. */
export const InlineAdminToolbar = AdminStationToolbar
/** Backwards-compatible short alias for consumers that call it AdminToolbar. */
export const AdminToolbar = AdminStationToolbar

export default AdminStationToolbar
